<script>
import 'swiper/css';
import { Swiper } from 'swiper';
import { Navigation, A11y } from 'swiper/modules';
import CldnImg from '@/components/CldnImg';
import CtaArrow from '@/components/common/CtaArrow';
import ArrowBtnNav from '@/components/common/ArrowBtnNav';
import { stripHtml } from '@/utils/common';

// Register Swiper modules once at module level
Swiper.use([Navigation, A11y]);

export default defineNuxtComponent({
  components: { ArrowBtnNav, CtaArrow, CldnImg },
  props: {
    teasers: {
      type: Array,
      default: () => [],
    },
    blockPosition: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      swiperInstance: null,
      isFirstSlide: true,
      isLastSlide: false,
      currentSlide: 0,
    };
  },

  mounted() {
    if (this.teasers.length > 1) {
      this.initSwiper();
    }
  },

  beforeUnmount() {
    if (this.swiperInstance) {
      this.swiperInstance.destroy();
      this.swiperInstance = null;
    }
  },

  methods: {
    initSwiper() {
      const container = this.$el && this.$el.querySelector('.teaser-swiper');
      if (!container) {
        return;
      }

      this.swiperInstance = new Swiper(container, {
        slidesPerView: 1,
        spaceBetween: 0,
        loop: false,
        grabCursor: true,
        autoHeight: false,
        on: {
          slideChange: (swiper) => {
            this.isFirstSlide = swiper.isBeginning;
            this.isLastSlide = swiper.isEnd;
            this.currentSlide = swiper.activeIndex;
          },
          init: (swiper) => {
            this.isFirstSlide = swiper.isBeginning;
            this.isLastSlide = swiper.isEnd;
            this.currentSlide = swiper.activeIndex;
          },
        },
      });
    },

    prevSlide() {
      if (this.swiperInstance) {
        this.swiperInstance.slidePrev();
      }
    },

    nextSlide() {
      if (this.swiperInstance) {
        this.swiperInstance.slideNext();
      }
    },

    gtmData(payload, index, link = null) {
      const promotion = stripHtml(payload.heading);

      return {
        promotion_id: promotion?.toLowerCase().replace(/ /g, '-'),
        promotion_name: promotion,
        creative_name: 'teaserWithMultipleCtas',
        creative_slot: [this.$route?.name, this.blockPosition + 1, index + 1, link?.text].join(' / '),
      };
    },
  },
});
</script>

<template>
  <div v-if="teasers.length" class="my-20">
    <!-- Single teaser without Swiper -->
    <div v-if="teasers.length === 1" class="container">
      <div
        v-track:view="gtmData(teasers[0], 0)"
        data-test-id="teaser-single"
        class="flex flex-col lg:flex-row w-full h-full"
      >
        <div class="lg:w-1/2 w-full relative overflow-hidden image-container">
          <CldnImg
            v-if="teasers[0].image[0]"
            :src="teasers[0].image[0].url"
            sizes="100vw sm:100vw lg:50vw lg:50vw xl:50vw"
            crop="fill"
            :alt="teasers[0].alt ? teasers[0].alt : teasers[0].image[0].title"
            class="w-full h-full object-cover object-center"
          />
        </div>
        <div class="lg:w-1/2 bg-blackly p-6 lg:p-12 text-white flex flex-col flex-auto w-full">
          <div class="text-container">
            <h2 class="my-4 fluid-3xl leading-tightest" v-html="teasers[0].heading"></h2>
            <div class="fluid-base leading-relaxed font-light" v-html="teasers[0].body"></div>
          </div>
          <div class="lg:flex lg:flex-row lg:justify-between lg:flex-wrap grid grid-cols-2 mt-4">
            <div v-for="({ articleLink }, i) in teasers[0].articleLinks" :key="i" class="lg:my-0 my-2">
              <CtaArrow
                v-track:click="gtmData(teasers[0], 0, articleLink)"
                v-bind="articleLink[0]"
                class="text-cta-arrow font-medium focus-visible:outline-white!"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Multiple teasers with Swiper -->
    <div v-else class="swiper teaser-swiper">
      <div class="swiper-wrapper">
        <div v-for="(teaser, index) in teasers" :key="index" class="swiper-slide">
          <div class="container">
            <div
              v-track:view="gtmData(teaser, index)"
              data-test-id="teaser-carousel"
              class="flex flex-col lg:flex-row w-full h-full"
            >
              <div class="lg:w-1/2 w-full relative overflow-hidden image-container">
                <CldnImg
                  v-if="teaser.image[0]"
                  :src="teaser.image[0].url"
                  sizes="100vw sm:100vw lg:50vw lg:50vw xl:50vw"
                  crop="fill"
                  :alt="teaser.alt ? teaser.alt : teaser.image[0].title"
                  class="w-full h-full object-cover object-center"
                />
              </div>
              <div class="lg:w-1/2 bg-blackly p-6 lg:p-12 text-white flex flex-col flex-auto w-full">
                <div>
                  <ArrowBtnNav
                    :left-arrow-disabled="isFirstSlide"
                    :right-arrow-disabled="isLastSlide"
                    :inverted="true"
                    @onLeftClick="prevSlide"
                    @onRightClick="nextSlide"
                  />
                  <div class="text-container">
                    <h2 class="my-4 fluid-3xl leading-tightest" v-html="teaser.heading"></h2>
                    <div class="fluid-base leading-relaxed font-light" v-html="teaser.body"></div>
                  </div>
                </div>
                <div class="lg:flex lg:flex-row lg:justify-between lg:flex-wrap grid grid-cols-2 mt-4 lg:">
                  <div v-for="({ articleLink }, i) in teaser.articleLinks" :key="i" class="lg:my-0 my-2">
                    <CtaArrow
                      v-track:click="gtmData(teaser, index, articleLink)"
                      v-bind="articleLink[0]"
                      class="text-cta-arrow font-medium focus-visible:outline-white!"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.teaser-swiper {
  width: 100%;
}

.swiper-wrapper {
  align-items: stretch;
}

.swiper-slide {
  height: auto;
  display: flex;
  flex-direction: column;
}

.swiper-slide > .container {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.image-container {
  aspect-ratio: 3 / 2;
}

@media (min-width: 1024px) {
  .image-container {
    aspect-ratio: 5 / 4;
    min-height: 100%;
  }
}

.text-container :deep(a) {
  text-decoration: underline;
}
</style>
